import React from "react";
import { resume, resumeAnchor } from "../data/resume";

// One titled block: title with a right-aligned date (stacked when compact), optional subtitle and body.
const Entry = ({ id, compact, title, subtitle, meta, children }) => (
  <div className="resume-entry" id={id}>
    <div className={compact ? "" : "flex items-baseline justify-between gap-6"}>
      <p className="resume-entry-title">{title}</p>
      {meta && <p className="resume-meta shrink-0">{meta}</p>}
    </div>
    {subtitle && <p className="resume-entry-subtitle">{subtitle}</p>}
    {children}
  </div>
);

// Renders the resume in Apple Notes' Title / Heading / Body styles.
// `compact` stacks dates under titles for the phone layout.
const Resume = ({ compact = false }) => (
  <article className={`resume ${compact ? "resume--compact" : ""}`}>
    <header>
      <h1 className="resume-title">{resume.name}</h1>
      <p className="resume-role">{resume.role}</p>
      <dl className="resume-contacts">
        {resume.contacts.map((c) => (
          <div key={c.label}>
            <dt>{c.label}</dt>
            <dd>
              <a href={c.href} target="_blank" rel="noreferrer">
                {c.text}
              </a>
            </dd>
          </div>
        ))}
      </dl>
    </header>

    <section id={resumeAnchor("Education")}>
      <h2 className="resume-heading">Education</h2>
      {resume.education.map((e) => (
        <Entry key={e.school} id={resumeAnchor(e.school)} compact={compact} title={e.school} meta={e.date}>
          <p className="resume-entry-subtitle">{e.degree}</p>
          <p className="resume-meta">{e.location}</p>
        </Entry>
      ))}
    </section>

    <section id={resumeAnchor("Technical skills")}>
      <h2 className="resume-heading">Technical skills</h2>
      <dl className="resume-skills">
        {resume.skills.map((s) => (
          <div key={s.label}>
            <dt>{s.label}</dt>
            <dd>{s.value}</dd>
          </div>
        ))}
      </dl>
    </section>

    <section id={resumeAnchor("Work experience")}>
      <h2 className="resume-heading">Work experience</h2>
      {resume.experience.map((job) => (
        <Entry
          key={job.company}
          id={resumeAnchor(job.company)}
          compact={compact}
          title={job.role}
          subtitle={job.company}
          meta={job.dates}
        >
          <ul className="resume-points">
            {job.points.map((point) => (
              <li key={point.slice(0, 40)}>{point}</li>
            ))}
          </ul>
        </Entry>
      ))}
    </section>

    <section id={resumeAnchor("Open-source projects")}>
      <h2 className="resume-heading">Open-source projects</h2>
      {resume.projects.map((p) => (
        <Entry key={p.name} id={resumeAnchor(p.name)} compact={compact} title={p.name} meta={p.date}>
          <p className="resume-body">{p.description}</p>
        </Entry>
      ))}
    </section>

    <section id={resumeAnchor("Certifications")}>
      <h2 className="resume-heading">Certifications</h2>
      {resume.certifications.map((c) => (
        <Entry key={c.id} id={resumeAnchor(c.name)} compact={compact} title={c.name}>
          <p className="resume-meta">Validation No: {c.id}</p>
        </Entry>
      ))}
    </section>
  </article>
);

export default Resume;
